import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// サーバーサイドで持っているデータがHEXカラーで返ってくるので、
// HEXカラーから名前付きカラーへのマッピング
const COLOR_MAP = {
  '#F6F6F6': 'gray',
  '#FFFAE2': 'yellow',
  '#FFE2F9': 'magenta',
  '#EFE6FF': 'purple',
  '#E4FFF5': 'green',
  '#DFE4FF': 'blue',
  '#FFE5D9': 'orange',
  '#FFDBDD': 'red',
} as const;

type HexColor = keyof typeof COLOR_MAP;

const tagVariants = cva(
  'inline-flex items-center text-nowrap rounded-full px-sm font-normal transition-colors focus:outline-hidden focus:ring-2 focus:ring-offset-2',
  {
    variants: {
      color: {
        default: 'border border-solid border-gray-200 bg-white',
        primary: 'border-primary bg-primary_lighten font-bold text-primary',
        danger: 'bg-danger_lighten font-bold text-danger',
        gray: 'bg-tag-gray text-tag-gray',
        blue: 'bg-tag-blue text-tag-blue',
        green: 'bg-tag-green text-tag-green',
        yellow: 'bg-tag-yellow text-tag-yellow',
        orange: 'bg-tag-orange text-tag-orange',
        red: 'bg-tag-red text-tag-red',
        magenta: 'bg-tag-magenta text-tag-magenta',
        purple: 'bg-tag-pueple text-tag-purple',
      },
      size: {
        sm: 'h-5 text-tag-sm',
        md: 'h-6 text-tag-sm',
        lg: 'h-8 text-tag-md',
      },
    },
    defaultVariants: {
      color: 'default',
      size: 'md',
    },
  }
);

export type TagColor = VariantProps<typeof tagVariants>['color'] | HexColor;

export interface TagProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    Omit<VariantProps<typeof tagVariants>, 'color'> {
  color?: TagColor;
}

function Tag({ className, color, size, children, ...props }: TagProps) {
  // HEXカラーの場合は名前付きカラーに変換
  const namedColor =
    typeof color === 'string' && color.startsWith('#')
      ? COLOR_MAP[color as HexColor] || 'default'
      : (color as VariantProps<typeof tagVariants>['color']);

  return (
    <div className={cn(tagVariants({ color: namedColor, size }), 'group', className)} {...props}>
      {children}
    </div>
  );
}

export { Tag, tagVariants };
